import { IconCross, IconTick } from '@newfront-insurance/core-ui';
// eslint-disable-next-line no-restricted-imports
import { cn } from '@newfront-insurance/core-ui/v2';
import { useEffect, type JSX } from 'react';

import { useNotifications } from '../context';
import { DEFAULT_ERROR_DURATION, DEFAULT_SUCCESS_DURATION } from '../lib/duration';
import type { Notification } from '../types';

interface Props {
  id: string;
  notification: Notification;
}

export function NotificationItem(props: Props): JSX.Element {
  const { id, notification } = props;
  const { title, type } = notification;
  const defaultDuration = type === 'error' ? DEFAULT_ERROR_DURATION : DEFAULT_SUCCESS_DURATION;
  const { duration = defaultDuration } = notification;
  const notifications = useNotifications();

  useEffect(() => {
    if (duration <= 0) return () => undefined;
    const timeout = setTimeout(() => notifications.remove(id), duration);
    return (): void => {
      clearTimeout(timeout);
    };
  }, [duration, id, notifications]);

  return (
    <div
      className={cn(
        'shadow-floating-bar relative mb-2 flex max-w-[400px] min-w-[300px] items-center rounded-xs p-4 pr-10 whitespace-pre-line text-white',
        type === 'success' && 'bg-grass-400',
        type === 'error' && 'bg-fire-500',
        type !== 'success' && type !== 'error' && 'bg-[#042553]',
      )}
    >
      <div className="flex w-full flex-row items-center gap-4" data-testId="notification-box">
        {type === 'success' && <IconTick size={16} />}
        {type === 'error' && (
          <button
            type="button"
            onClick={() => notifications.remove(id)}
            className="flex items-center justify-center rounded-sm p-1 hover:bg-white/20 focus:ring-2 focus:ring-white/50 focus:outline-hidden"
            aria-label="Dismiss notification"
            data-testid="notification-dismiss"
          >
            <IconCross color="#ffffff" size={16} />
          </button>
        )}
        <span
          className={`text-center break-words text-white ${
            type === 'success' || type === 'error' ? 'text-sm leading-[18px]' : 'text-base leading-[21px]'
          }`}
          data-testId="notification-text"
        >
          {title}
        </span>
      </div>
    </div>
  );
}
